import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BlogForm, { EMPTY_POST } from './BlogForm';
import { fetchBlogs, fetchBlogById } from '../../api/blogs';
import { deleteBlog, saveBlog } from '../../api/blogAdmin';
import { ALL_CATEGORIES } from '../../constants/blogCategories';
import { imageFallback } from '../../utils/env';
import { blogPath } from '../../utils/slug';
import { formatDate, publishedAt, readingMinutes } from '../../utils/post';

const PAGE_LIMIT = 10;

/**
 * Posts section of the admin panel: the create/edit form plus the list of
 * existing posts.
 *
 * Reads go through the same public API the site uses (src/api/blogs.js), so the
 * list here always reflects exactly what a reader would see. Writes go through
 * src/api/blogAdmin.js.
 */
function BlogsSection() {
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterCategory, setFilterCategory] = useState('All');
  const [message, setMessage] = useState(null);

  const [editing, setEditing] = useState(null); // the post being edited, or null
  // Bumping this remounts BlogForm, which resets its internal state to the new
  // `initial`. BlogForm holds a working copy so typing does not re-render the
  // whole list on every keystroke — the cost is that it needs an explicit reset.
  const [formKey, setFormKey] = useState(0);

  const formRef = useRef(null);

  const load = useCallback(
    async (nextCursor, replace) => {
      replace ? setLoading(true) : setLoadingMore(true);
      try {
        const data = await fetchBlogs({
          category: filterCategory,
          limit: PAGE_LIMIT,
          cursor: nextCursor,
        });
        setPosts((prev) => (replace ? data.blogs : [...prev, ...data.blogs]));
        setCursor(data.cursor);
        setHasMore(data.hasMore);
      } catch (err) {
        setMessage({ type: 'error', text: err.message });
      } finally {
        replace ? setLoading(false) : setLoadingMore(false);
      }
    },
    [filterCategory]
  );

  // The cursor only applies to the filter that produced it, so changing the
  // category restarts from the beginning.
  useEffect(() => {
    setPosts([]);
    setCursor(null);
    setHasMore(false);
    load(null, true);
  }, [load]);

  const resetForm = () => {
    setEditing(null);
    setFormKey((k) => k + 1);
  };

  const handleSave = async (post) => {
    // Throwing propagates to BlogForm, which shows the message inline next to
    // the fields rather than at the top of the page where it can scroll away.
    const saved = await saveBlog(post);
    await load(null, true);
    resetForm();
    setMessage({
      type: 'ok',
      text: post.blogId ? `Updated “${saved.title}”.` : `Published “${saved.title}”.`,
      link: blogPath(saved),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEdit = async (post) => {
    setMessage(null);
    try {
      // Fetch full detail to get content field (list only returns summary)
      const fullPost = await fetchBlogById(post.blogId, post.category);
      if (!fullPost) {
        setMessage({ type: 'error', text: 'Could not load post for editing.' });
        return;
      }
      setEditing({ ...EMPTY_POST, ...fullPost, tags: fullPost.tags || [] });
      setFormKey((k) => k + 1);
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to load post.' });
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete “${post.title}”? This cannot be undone.`)) return;
    try {
      await deleteBlog(post.blogId);
      if (editing?.blogId === post.blogId) resetForm();
      await load(null, true);
      setMessage({ type: 'ok', text: `Deleted “${post.title}”.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="admin-section">
      {message && (
        <div
          className={`admin-msg ${message.type === 'error' ? 'is-error' : 'is-ok'}`}
          role={message.type === 'error' ? 'alert' : 'status'}
        >
          {message.text}
          {message.link && (
            <>
              {' '}
              <Link to={message.link} target="_blank" rel="noopener noreferrer">
                View the live page →
              </Link>
            </>
          )}
        </div>
      )}

      <div ref={formRef}>
        <BlogForm
          key={formKey}
          initial={editing || EMPTY_POST}
          isEditing={Boolean(editing)}
          onSave={handleSave}
          onCancel={resetForm}
        />
      </div>

      <div className="admin-card">
        <div className="admin-card-head">
          {/* Loaded count, not a total: the API is cursor-paged and cannot
              report how many posts match without reading them all. */}
          <h2 className="admin-card-title">
            Posts{' '}
            {posts.length > 0 && (
              <span className="admin-count">
                {posts.length}
                {hasMore ? '+' : ''}
              </span>
            )}
          </h2>
          <div className="admin-card-head-actions">
            <label className="sr-only" htmlFor="admin-filter">
              Filter by category
            </label>
            <select
              id="admin-filter"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="All">All categories</option>
              {ALL_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="state-msg">
            <p>Loading…</p>
          </div>
        ) : !posts.length ? (
          <div className="state-msg">
            <p>No posts yet. Publish one using the form above.</p>
          </div>
        ) : (
          <ul className="admin-list">
            {posts.map((p) => (
              <li key={p.blogId} className="admin-list-item">
                <img
                  src={p.image || '/default-blog.svg'}
                  alt=""
                  className="admin-list-thumb"
                  loading="lazy"
                  onError={imageFallback('/default-blog.svg')}
                />
                <div className="admin-list-main">
                  <strong>
                    {p.featured && (
                      <span className="chip chip-sm admin-featured">Featured</span>
                    )}
                    {p.title}
                  </strong>
                  <small>
                    {p.category} · {formatDate(publishedAt(p))} · {p.author} ·{' '}
                    {readingMinutes(p)} min
                  </small>
                </div>
                <div className="admin-list-actions">
                  <Link
                    className="btn btn-ghost btn-sm"
                    to={blogPath(p)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleEdit(p)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDelete(p)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {hasMore && (
          <div className="load-more-wrap">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={loadingMore}
              onClick={() => load(cursor, false)}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogsSection;
